import { useNutritionDataActions, useNutritionLoadingState } from '@/hooks/useNutritionStoreHooks'
import { Leaf } from 'lucide-react'
import { ReactNode, useEffect } from 'react'

/**
 * Props for NutritionDataProvider component
 */
interface NutritionDataProviderProps {
  /**
   * Child components to render after data is loaded
   */
  children: ReactNode

  /**
   * Optional custom loading component
   */
  loadingComponent?: ReactNode
}

/**
 * Provider component that loads nutrition data before rendering children
 * This prevents flickering by ensuring data is loaded before any child components mount
 *
 * @param props - Component props
 * @returns Provider component with loading state management
 */
export function NutritionDataProvider({ children, loadingComponent }: NutritionDataProviderProps) {
  const loadingState = useNutritionLoadingState()
  const { loadData } = useNutritionDataActions()

  // Load data on mount
  useEffect(() => {
    // Only load if we haven't loaded yet
    if (loadingState === 'idle') {
      loadData()
    }
  }, [loadData, loadingState])

  // Show loading state while data is being loaded
  // This ensures children don't render until data (and calculated dates) are ready
  if (loadingState === 'loading' || loadingState === 'idle') {
    if (loadingComponent) {
      return <>{loadingComponent}</>
    }

    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Leaf className="h-12 w-12 animate-pulse mx-auto text-muted-foreground mb-4" />
          <p className="text-lg text-muted-foreground">Loading nutrition data...</p>
        </div>
      </div>
    )
  }

  // If error occurred, still render children (they can handle no data gracefully)
  // This prevents the entire app from being blocked by a loading error
  return <>{children}</>
}
